import { useState, useEffect, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import { history } from 'umi';
import PcContext from '@/utils/context';
import { message, Typography, Modal, DatePicker, RangePicker } from 'iglooform';
import { Tooltip } from 'antd';
import { InfoFilled } from 'iglooicon';
import './index.less';
import EarningList from '@/components/earnings/earnings-list';
import { aesEncrypt, handleOnScroll } from '@/utils/common';
import { getCommissions, getCommissionBalance, getAccounts } from './service';
import CashOutCard from '@/components/cash-out-card/cash-out-card';
import SkeletonLoading from '@/components/skeleton-loading';
import EmptyView from '@/components/empty-view/empty-view';
import moment, { Moment } from 'moment';
import en from 'antd/es/date-picker/locale/en_US';
import vn from 'antd/es/date-picker/locale/vi_VN';

interface IEarnings {
  commissions: EarningsListProps[] | [];
  cursor: string;
}

function getInitStartDate() {
  // 读缓存
  const startDateCache = localStorage.getItem(BUSINESS_OVERVIEW_STARTDATE);
  if (startDateCache) {
    return +startDateCache;
  }
  // 当月1号
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  return parseInt(String(new Date(year, month, 1).getTime() / 1000), 10);
}

function getInitEndDate() {
  // 读缓存
  const endDateCache = localStorage.getItem(BUSINESS_OVERVIEW_ENDDATE);
  if (endDateCache) {
    return +endDateCache;
  }
  // 当天
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const endDateStartDay = parseInt(
    String(new Date(year, month, day).getTime() / 1000),
    10,
  );
  // end date 需要是当天的23:59:59
  return (
    moment(endDateStartDay * 1000)
      .add(1, 'day')
      .unix() - 1
  );
}

const Earnings = () => {
  const { formatMessage } = useIntl();
  const isPc = useContext(PcContext);
  const [cashOutLoading, setCashOutLoading] = useState<boolean>(false);
  const [balance, setBalance] = useState<EarningBalanceProps>();

  const [startDate, setStartDate] = useState(getInitStartDate());
  const [endDate, setEndDate] = useState(getInitEndDate());
  const currentLang = localStorage.getItem('selected_lang') || 'vi-VN';

  const [isloading, setLoading] = useState<boolean>(false);

  const earningsCursor = useRef('0');

  const changeStartDateValue = (date: Moment) => {
    const startDate = Math.floor(+date.startOf('day') / 1000);
    setStartDate(startDate);
    // 写进缓存
    localStorage.setItem(BUSINESS_OVERVIEW_STARTDATE, String(startDate));
    return startDate;
  };

  const changeEndDateValue = (date: Moment) => {
    const endDate = Math.floor(+date.endOf('day') / 1000);
    setEndDate(endDate);
    // 写进缓存
    localStorage.setItem(BUSINESS_OVERVIEW_ENDDATE, String(endDate));
    return endDate;
  };

  const onChange = (dates: [Moment, Moment]) => {
    const [momentStartDate, momentEndDate] = dates;
    const changeStartDate = changeStartDateValue(momentStartDate);
    const changeEndDate = changeEndDateValue(momentEndDate);
    earningsCursor.current = '0';
    getEarningsList([changeStartDate, changeEndDate], false);
  };

  const pcPicker = (
    <RangePicker
      className={'rangePicker'}
      allowClear={false}
      value={[moment(startDate * 1000), moment(endDate * 1000)]}
      onChange={(dates) => onChange(dates as [Moment, Moment])}
      locale={currentLang === 'en-US' ? en : vn}
    />
  );

  const onChangeStartH5 = (date: Moment) => {
    changeStartDateValue(date);
  };

  const onChangeEndH5 = (date: Moment) => {
    changeEndDateValue(date);
  };

  const h5Picker = (
    <div className="h5Picker">
      <DatePicker
        suffixIcon={null}
        format="DD/MM/YYYY"
        allowClear={false}
        value={moment(startDate * 1000)}
        onChange={(date) => onChangeStartH5(date as Moment)}
        locale={currentLang === 'en-US' ? en : vn}
      />
      <div className="h5PickerLine"></div>
      <DatePicker
        suffixIcon={null}
        format="DD/MM/YYYY"
        allowClear={false}
        value={moment(endDate * 1000)}
        onChange={(date) => onChangeEndH5(date as Moment)}
        locale={currentLang === 'en-US' ? en : vn}
      />
    </div>
  );

  const renderPicker = isPc ? pcPicker : h5Picker;

  const [earningsList, setEarningsList] = useState<IEarnings>();
  const checkBankAccount = () => {
    setCashOutLoading(true);
    getAccounts()
      .then((res) => {
        if (res) {
          const accounts = res.accounts ?? [];
          if (accounts.length) {
            gotoCashOut();
          } else {
            Modal.confirm({
              title: formatMessage({ id: 'Default Receiving Account' }),
              content: formatMessage({
                id: 'To cash out, please set a default receiving account first',
              }),
              okText: formatMessage({ id: 'Set Now' }),
              cancelText: formatMessage({ id: 'Cancel' }),
              onOk: () => {
                history.push(
                  `/me/verify-payment-password/cashout-change-account`,
                );
              },
            });
          }
        }
      })
      .finally(() => {
        setCashOutLoading(false);
      });
  };

  const gotoCashOut = () => {
    if (!balance) return;
    const min = parseInt(balance.min_withdraw_amount, 10);
    const available = parseInt(balance.available, 10);
    if (available < min) {
      const warningMessage = formatMessage({
        id: 'The minimum amount for withdrawal is 100,000 VND, please make sure your total amount is above 100,000 VND and try again',
      });
      message.warning(warningMessage);
    } else {
      history.push({
        pathname: '/earnings/cash-out',
        query: {
          balance: aesEncrypt(balance.available),
          minWithdrawAmount: aesEncrypt(balance.min_withdraw_amount),
        },
      });
    }
  };

  const getEarningsList = (dates: any, isScroll: boolean) => {
    if (window.location.pathname !== '/earnings') return;
    if (earningsCursor.current === '') return;
    if (earningsCursor.current === '0' && isScroll) return;
    const [changeStartDate, changeEndDate] = dates;
    getCommissions({
      cursor: earningsCursor.current,
      size: 20,
      currency: 'VND',
      start: changeStartDate || startDate,
      end: changeEndDate || endDate,
    })
      .then((res) => {
        if (res) {
          if (earningsCursor.current === '0') {
            setEarningsList(res);
          } else {
            if (res.commissions) {
              setEarningsList((newObj: any) => {
                return {
                  commissions: [...newObj.commissions, ...res.commissions],
                  cursor: res.cursor,
                };
              });
            }
          }
          earningsCursor.current = res.cursor;
        }
      })
      .finally(() => {
        setLoading(true);
      });
  };

  const getCommissionsBalance = () => {
    getCommissionBalance()
      .then((res) => {
        if (res) {
          setBalance(res);
        }
      })
      .finally(() => {
        setLoading(true);
      });
  };

  useEffect(() => {
    const dates = [startDate, endDate];
    window.addEventListener(
      'scroll',
      handleOnScroll(() => {
        getEarningsList(dates, true);
      }),
    );

    window.gtag('event', 'view_earning', {
      user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')?.id,
    });

    return () => {
      window.addEventListener(
        'scroll',
        handleOnScroll(() => {
          getEarningsList(dates, true);
        }),
      );
    };
  }, []);

  useEffect(() => {
    const dates = [startDate, endDate];
    getEarningsList(dates, false);
    getCommissionsBalance();
  }, []);

  return (
    <>
      {isloading && !cashOutLoading ? (
        <section
          className="cash-out-container"
          style={isPc.isPc ? {} : { marginTop: '24px' }}
        >
          {/* {renderPicker} */}
          {/* <Tooltip
            title={
              <Typography level="body2">
                {formatMessage({
                  id: 'The data is shown as from start date 00:00:00 to end date 23:59:59.',
                })}
              </Typography>
            }
            color="#fff"
            overlayInnerStyle={{ color: '#212121' }}
            trigger={isPc ? 'hover' : 'click'}
            placement={isPc ? 'top' : 'bottomRight'}
          >
            <InfoFilled style={{ fontSize: '24px', color: '#666' }} />
          </Tooltip> */}
          <CashOutCard
            cashOutLoading={cashOutLoading}
            balance={balance}
            checkBankAccount={checkBankAccount}
            currentLang={currentLang}
            currency={formatMessage({ id: 'VND' })}
            isPc={isPc.isPc}
          />
          <section className="earnings-list-container">
            <section className="earnings-title">
              <Typography level="h2">
                {formatMessage({ id: 'Latest Earnings' })}
              </Typography>
            </section>
            <section className="earnings-list">
              {earningsList && !!earningsList.commissions.length ? (
                <EarningList
                  isPoints={false}
                  data={earningsList.commissions}
                  isPc={isPc.isPc}
                  currentLang={currentLang}
                />
              ) : (
                <EmptyView
                  text={formatMessage({ id: 'You have no earnings now.' })}
                />
              )}
            </section>
          </section>
        </section>
      ) : (
        <div style={{ marginTop: '100px' }}>
          <SkeletonLoading />
        </div>
      )}
    </>
  );
};

export default Earnings;
